import {useEffect, useMemo, useState} from "react";
import {Button} from "../../../basic/button.tsx";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "../../../basic/dialog.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {LicenseSelector} from "../../../shared/selectors/LicenseSelector.tsx";
import {InviteLinkDisplay} from "./InviteLinkDisplay.tsx";
import {useGenerateInvitationMutation, useGetPortalLicensesQuery} from "../../../../store/generated/api.ts";
import {toast} from "sonner";
import {TriangleAlert} from "lucide-react";
import {Alert, AlertDescription} from "../../../basic/alert.tsx";
import {filterAvailableLicenses} from "../../../../utils/licenseUtils";

interface InviteTeamMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function InviteTeamMemberDialog({open, onOpenChange}: InviteTeamMemberDialogProps) {
    const {t} = useTranslationWithKeys();
    const {handleApiError} = useErrorHandler();
    const {data: portalLicenses, isLoading: isLoadingLicenses} = useGetPortalLicensesQuery({});

    const availableLicenses = useMemo(() => {
        if (!portalLicenses?.licenses) return [];
        return filterAvailableLicenses(portalLicenses.licenses);
    }, [portalLicenses]);
    const [selectedLicense, setSelectedLicense] = useState<string>("");
    const [inviteCode, setInviteCode] = useState<string>("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open || selectedLicense || availableLicenses.length === 0) return;
        setSelectedLicense(availableLicenses[0].id ?? "none");
    }, [open, selectedLicense, availableLicenses]);

    const [generateInvitation, {isLoading}] = useGenerateInvitationMutation();

    const selectedLicenseData = availableLicenses.find(license => license.id === selectedLicense);

    const generateInviteLink = async () => {
        try {
            const licenseId = selectedLicenseData?.id || "";
            const response = await generateInvitation({
                vwpGenerateInvitationRequest: {
                    licenseId
                }
            }).unwrap();

            if (response.invitationCode) {
                setInviteCode(response.invitationCode);
                setCopied(false);
                toast.success(t('team.invitationGenerated'));
            } else {
                toast.error(t('team.invitationGenerationFailed'));
            }
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('team.invitationGenerationFailed')});
        }
    };

    return (
        <Dialog open={open} onOpenChange={(open) => {
            onOpenChange(open);
            if (!open) {
                setSelectedLicense("");
                setInviteCode("");
                setCopied(false);
            }
        }}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('team.generateInviteLink')}</DialogTitle>
                    <DialogDescription>
                        {t('team.generateInviteLinkDescription')}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <LicenseSelector
                        value={selectedLicense}
                        onValueChange={setSelectedLicense}
                        licenses={availableLicenses}
                        label={t('team.assignLicenseOptional')}
                        placeholder={t('common.team.noLicenseAssigned')}
                        showNoneOption={true}
                        disabled={isLoadingLicenses}
                        actionButton={{
                            text: t('common.action.generate'),
                            onClick: generateInviteLink,
                            disabled: isLoading || isLoadingLicenses,
                            loading: isLoading
                        }}
                        description={selectedLicenseData
                            ? t('team.autoAssignLicense', {license: selectedLicenseData.product || selectedLicenseData.id})
                            : undefined
                        }
                    />

                    {!selectedLicenseData && !isLoadingLicenses && (
                        <Alert className="border-warning [&>svg]:text-warning">
                            <TriangleAlert/>
                            <AlertDescription>{t('team.inviteWithoutLicenseWarning')}</AlertDescription>
                        </Alert>
                    )}

                    {inviteCode && (
                        <InviteLinkDisplay
                            inviteCode={inviteCode}
                            copied={copied}
                            onCopiedChange={setCopied}
                        />
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.action.done')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export type {InviteTeamMemberDialogProps};